/**
 * This component renders the schedule page and is used by ClubSchedule, GameCentreSchedule, and DivisionSchedule
 */
import { useState } from "react";
import GameOverview from "./GameOverview";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import Loading from "./Loading";
import DropdownSelect from "./DropdownSelect";

const getGameStatus = (played) => {
  switch (played) {
    case -1:
      return "Provisional";
    case 0:
      return "Preview";
    case 1:
      return "Played";
    case 2:
      return "Pending";
    case 3:
      return "Pending";
    // 4 = Fixture Issue Raised
    case 4:
      return "Preview"
    case 5:
      return "Postponed";
    case 6:
      return "Cancelled";
    case 7:
      return "Abandoned";
    default:
      return "Preview";
  }
};

const Schedule = ({
  mode,
  sidebar,
  seasons,
  teams = [],
  clubs = [],
  id = null,
  paging = true,
  season_offset = 0,
  is_nationals = false
}) => {
  const [season, setSeason] = useState(seasons[season_offset].value);
  const [filter, setFilter] = useState("all");
  const [teamName, setTeamName] = useState("all");
  // const [display, setDisplay] = useState(season_offset === 0 ? is_nationals ? "upcoming" : "recent" : "all");
  const [display, setDisplay] = useState("all");
  const [allGames, setAllGames] = useState([]);
  const [offset, setOffset] = useState(0);

  const processGameData = (data) => {
    let game_ids = allGames.map((g) => g.id);

    if (paging) setAllGames([]);

    data.games.forEach((g) => {
      if (!game_ids.includes(g.id)) allGames.push(g);
    });

    return {
      season: data.season,
      games: allGames, //.sort((a, b) => a.date - b.date),
      has_more: data.games.length > 0,
    };
  };

  const previous = () => {
    if (offset > 0) setOffset((offset) => offset - 1);
  };

  const next = () => {
    setOffset((offset) => offset + 1);
  };

  const url = mode === "all" ? "all" : `${mode}/${id}`;

  const fetchGames = (mode, id, season, offset, filter, display) =>
    fetch(
      `${process.env.REACT_APP_BUIHA_API_URL}/schedule/${url}/?season=${season}&offset=${offset}&filter=${filter}&display=${display}`
    )
      .then((res) => res.json())
      .then((json) => {
        if (offset === 0) {
          allGames.length = 0;
        }
        return processGameData(json);
      })
      .then((json) => {
        if (json.has_more && !paging) {
          setOffset((offset) => offset + 1);
        }
        return json;
      });
  const { isPlaceholderData, error, data, isFetching } = useQuery({
    staleTime: Infinity,
    queryKey: ["schedule", mode, id, season, offset, filter, display],
    queryFn: () => fetchGames(mode, id, season, offset, filter, display),
    keepPreviousData: true,
    placeholderData: keepPreviousData,
  });

  const handleFilterChange = (e) => {
    setOffset(0);
    setFilter(e.target.value);

    const selectedValue = e.target.value;
    setTeamName(selectedValue.charAt(0).toUpperCase() + selectedValue.slice(1));

    teams.forEach((t) => {
      if (t.id.toString() === selectedValue) setTeamName(t.short);
    });
    clubs.forEach((t) => {
      if (t.id.toString() === selectedValue) setTeamName(t.name);
    });
  };

  // Options for "Display"
  const displayOptions = [
    { value: "upcoming", label: "Upcoming" },
    { value: "recent", label: "Recent" },
    { value: "all", label: "All Games" },
  ];

  const handleSeasonChange = (e) => {
    setOffset(0);
    setSeason(e.target.value);
    if (e.target.value !== seasons[0].value) {
      setDisplay("all");
    } else {
      setDisplay("upcoming");
    }
  };

  const handleDisplayChange = (e) => {
    setDisplay(e.target.value);
  };

  const loadingOptions = [{ value: "all", label: "Loading..." }];

  const seasonOptions = seasons.map((season) => ({
    value: season.value,
    label: season.title,
  }));

  // Options for "Filter"
  const filterOptions = [
    { value: "", label: "All Teams" },
    ...teams.map((team) => ({ value: team.id, label: team.short })),
    ...clubs.map((club) => ({ value: club.id, label: club.name })),
  ];

  if (mode !== 'division') {
    filterOptions.push({ value: "varsity", label: "Varsity" });
    filterOptions.push({ value: "challenge", label: "Challenge" });
  }

  if (!data)
    return (
      <>
        <div className="w-full flex flex-row items-start justify-center sm:justify-between mb-3">
          <div className="lg:block hidden flex flex-col items-start justify-start">
            <h2 className="hidden leading-tight sm:block text-left text-2xl font-semibold">
              Schedule
            </h2>
            <p className="hidden sm:block text-left dark-text text-darken font-light">
              Loading...
            </p>
          </div>

          <div className="flex flex-row items-start justify-start">
            {/* Select season - Disabled for now TODO implement */}
            <DropdownSelect
              label="Season"
              options={loadingOptions}
              value="all"
              disabled={true}
              className="mr-1 xs:mr-2"
            />

            <DropdownSelect
              label="Display"
              options={loadingOptions}
              value="all"
              disabled={true}
              className="mr-1 xs:mr-2" 
            />

            <DropdownSelect
              label="Team"
              options={loadingOptions}
              value="all"
              disabled={true}
            />

            <p className="xs:ml-2 ml-1 xs:mt-1 my-auto max-xs:text-xs border-buiha-red dark:border-dark-5 p-1 xs:p-2 xs:px-3 xs:mr-1 rounded border-2 opacity-25">
              &lt;
            </p>
            <h2 className="ml-2 mr-2 xs:mt-1 my-auto max-xs:text-xs border-buiha-red dark:border-dark-5 p-1 xs:p-2 xs:px-3 w5 text-center align-middle">
              {offset + 1}
            </h2>
            <p className="xs:ml-2 ml-1 xs:mt-1 my-auto max-xs:text-xs border-buiha-red dark:border-dark-5 p-1 xs:p-2 xs:px-3 xs:mr-1 rounded border-2 opacity-25">
              &gt;
            </p>
          </div>
        </div>
        <Loading />
      </>
    );

  if (error) return "An error has occurred: " + error.message;

  return (
    data && (
      <>
        <>
          <div className="w-full flex flex-row items-start justify-center sm:justify-between mb-3">
            <div className="lg:block flex flex-col items-start justify-start">
              <h2 className="hidden leading-tight sm:block text-left text-2xl font-semibold">
                Schedule
              </h2>
              <p className="hidden sm:block text-left dark-text text-darken font-light">
                <span>
                  <span className="capitalize">
                    {data.season} {display}
                  </span>
                  {teamName !== "all" ? ` ${teamName}` : ""} Games
                </span>
              </p>
            </div>

            <div className="flex flex-row items-start justify-start">
              {/* Select season - Disabled for now TODO implement */}
              {seasons.length > 1 ? (
                <DropdownSelect
                  label="Season"
                  options={seasonOptions}
                  value={season}
                  onChange={handleSeasonChange}
                  className="mr-1 xs:mr-2"
                  disabled={isFetching}
                />
              ) : (
                  <></>
              )}

              {/* Select display - Recent, all, upcoming */}
              <DropdownSelect
                label="Display"
                options={displayOptions}
                value={display}
                onChange={handleDisplayChange}
                className="mr-1 xs:mr-2"
                disabled={isFetching}
              />

              {/* Select team */}
              <DropdownSelect
                label="Filter Team"
                options={filterOptions}
                value={filter}
                onChange={handleFilterChange}
                disabled={isFetching}
              />
              {offset > 0 ? (
                <button
                  onClick={() => {
                    if (!isPlaceholderData) previous();
                  }}
                  className="ml-1 xs:ml-2 xs:mt-1 my-auto max-xs:text-xs border-buiha-red dark:border-dark-5 p-1 xs:p-2 xs:px-3 xs:mr-1 rounded border-2 hover:bg-buiha-red dark:hover:bg-dark-5 hover:text-white duration-200 ease-in-out"
                >
                  &lt;
                </button>
              ) : (
                <p className="xs:ml-2 ml-1 xs:mt-1 my-auto max-xs:text-xs border-buiha-red dark:border-dark-5 p-1 xs:p-2 xs:px-3 xs:mr-1 rounded border-2 opacity-25">
                  &lt;
                </p>
              )}

              <h2 className="ml-2 mr-2 xs:mt-1 my-auto max-xs:text-xs border-buiha-red dark:border-dark-5 p-1 xs:p-2 xs:px-3 font-bold w-5 text-center hidden sm:block">
                {offset + 1}
              </h2>

              {data.games.length >= 20 ? (
                <button
                  onClick={() => {
                    if (!isPlaceholderData) next();
                  }}
                  className="ml-1 xs:ml-2 xs:mt-1 my-auto max-xs:text-xs border-buiha-red dark:border-dark-5 p-1 xs:p-2 xs:px-3 xs:mr-1 rounded border-2 hover:bg-buiha-red dark:hover:bg-dark-5 hover:text-white duration-200 ease-in-out"
                >
                  &gt;
                </button>
              ) : (
                <p className="xs:ml-2 ml-1 xs:mt-1 my-auto max-xs:text-xs border-buiha-red dark:border-dark-5 p-1 xs:p-2 xs:px-3 xs:mr-1 rounded border-2 opacity-25">
                  &gt;
                </p>
              )}
            </div>
          </div>

          {isFetching ? (
              <Loading />
          ) : (
              <div
                  className={`${
                      data.games.length > 0
                          ? "border-b-2 border-light-3 dark:border-b-0 w-full"
                          : "w-full"
                  }`}
              >
                {/* FOR EACH GAME */}
                {data.games.map((game, index) => {
                    game.played_status = getGameStatus(game.played);
                    return (
                      <GameOverview key={game.id} gameData={game} index={index}/>
                  );
                })}
                {data.games.length === 0 ? (
                    <p className="my-8 text-center dark-text text-darken">
                      No games found
                    </p>
                ) : null}
              </div>
          )}
        </>

        {/* if sidebar is defined, render it */}
        {sidebar ? (
            <div className="xl:block max-xl:hidden">{sidebar}</div>
        ) : (
            <></>
        )}
      </>
    )
  );
};
export default Schedule;
